import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Component to display question validation rules in the form compiler
 * Shows validation rules for multiple choice questions between files and options
 * 
 * @param {Object} props - Component props
 * @param {Object} props.question - The question object containing validation rules
 * @param {Object} props.colors - Color scheme for styling
 * @returns {JSX.Element|null} Validation rules component or null if no rules
 */
const QuestionValidationRules = ({ question, colors = {} }) => {
  const { t } = useTranslation();

  // Only show for multiple choice questions with validation rules
  if (question.questionType !== 'multiple' && question.questionType !== 'pdca') {
    return null;
  }

  if (!question.validation || !Array.isArray(question.validation) || question.validation.length === 0) {
    return null;
  }

  /**
   * Gets the display text for an answer by its ID
   */
  const getAnswerText = (answerId) => {
    const answer = question.answers?.find(a => (a.answerId || a.id) === answerId);
    if (!answer) return `Answer ${answerId}`;
    
    // Handle localized text
    if (Array.isArray(answer.answerText)) {
      const localizedAnswer = answer.answerText.find(t => t.language === 'it') || answer.answerText[0];
      return localizedAnswer?.text || 'Answer text not available';
    }
    
    return answer.answerText || 'Answer text not available';
  };

  /**
   * Formats a condition text for display
   */
  const formatConditionText = (condition, isSource = true) => {
    if (isSource) {
      switch (condition) {
        case 'any_of':
          return t('anyOfThese', { ns: 'labels', defaultValue: 'almeno una di queste' });
        case 'all':
          return t('allOfThese', { ns: 'labels', defaultValue: 'tutte queste' });
        case 'not_in':
          return t('noneOfThese', { ns: 'labels', defaultValue: 'nessuna di queste' });
        default:
          return condition;
      }
    } else {
      switch (condition) {
        case 'mandatory_any':
          return t('mustSelectOneOf', { ns: 'labels', defaultValue: 'deve selezionare almeno una di' });
        case 'mandatory_all':
          return t('mustSelectAllOf', { ns: 'labels', defaultValue: 'deve selezionare tutte' });
        case 'excluded_all':
          return t('cannotSelect', { ns: 'labels', defaultValue: 'non può selezionare' });
        default:
          return condition;
      }
    }
  };

  /**
   * Renders a single validation rule
   */
  const renderRule = (rule, index) => {
    const {
      source_answers = [],
      source_condition,
      dest_answers = [],
      dest_condition
    } = rule;

    if (!source_answers.length || !dest_answers.length) {
      return null;
    }

    const sourceTexts = source_answers.map(getAnswerText);
    const destTexts = dest_answers.map(getAnswerText);

    return (
      <div key={index} className="validation-rule mb-2 p-3 rounded" style={{
        backgroundColor: colors?.primaryLight || '#fef0ea',
        border: `1px solid ${colors?.primary || '#f05f23'}20`
      }}>
        <div className="d-flex align-items-start">
          <div className="me-2">
            <i className="fa-solid fa-link" style={{ color: colors?.primary || '#f05f23' }}></i>
          </div>
          <div className="flex-grow-1">
            <div className="small text-muted mb-1">
              <strong>{t('validationRule', { ns: 'labels', defaultValue: 'Regola di validazione' })} {index + 1}</strong>
            </div>
            <div className="validation-rule-text">
              <span className="fw-bold">{t('if', { ns: 'labels', defaultValue: 'Se' })}</span>
              {' '}
              <span className="text-primary">{formatConditionText(source_condition, true)}</span>
              {' '}
              <span className="fst-italic">
                {sourceTexts.map((text, i) => (
                  <React.Fragment key={i}>
                    "{text}"
                    {i < sourceTexts.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </span>
              <br />
              <span className="fw-bold">{t('then', { ns: 'labels', defaultValue: 'allora' })}</span>
              {' '}
              <span className="text-danger">{formatConditionText(dest_condition, false)}</span>
              {' '}
              <span className="fst-italic">
                {destTexts.map((text, i) => (
                  <React.Fragment key={i}>
                    "{text}"
                    {i < destTexts.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="question-validation-rules mt-3">
      <div className="d-flex align-items-center mb-2">
        <i className="fa-solid fa-shield-check me-2" style={{ color: colors?.primary || '#f05f23' }}></i>
        <h6 className="mb-0" style={{ color: colors?.primary || '#f05f23' }}>
          {t('validationRules', { ns: 'labels', defaultValue: 'Regole di Validazione' })}
        </h6>
      </div>
      
      <div className="validation-rules-container">
        {question.validation.map((rule, index) => renderRule(rule, index))}
      </div>
      
      <div className="mt-2">
        <small className="text-muted">
          <i className="fa-solid fa-info-circle me-1"></i>
          {t('validationRulesNote', { 
            ns: 'labels', 
            defaultValue: 'Queste regole vengono applicate automaticamente durante la compilazione.' 
          })}
        </small>
      </div>
    </div>
  );
};

export default QuestionValidationRules;
